import type { ReactNode } from 'react'
import type { LabeledValue, DefaultOptionType } from 'antd/es/select'
import type { EasyRemoteProps, EasyRemoteSearch, EasyRemoteDropDown } from './declare'
import { useState, useEffect, useMemo, useRef } from 'react'
import { Pagination, Select, Divider, Spin, Typography } from 'antd'
import { get, debounce, merge, isEmpty, isEqual } from 'lodash'
import 'src/styles/components/common/easyRemoteSelect.less'

// 下拉框的额外渲染
const DropDownRender = (props: EasyRemoteDropDown) => {
  const [pagination, setPagination] = useState({ total: 0, current: 1 })
  useEffect(() => {
    setPagination(prev => isEqual(prev, props.pagination) ? prev : props.pagination)
  }, [props.pagination])

  const getExtraAddon = () => {
    const { addonDropdown } = props
    if (!addonDropdown?.show) return null
    if (addonDropdown.render instanceof Function) return addonDropdown.render()
    const className = `remote-select__dropdown ${addonDropdown.selected ? 'selected' : ''}`
    return (
      <div onClick={addonDropdown.onSelect} className={className}>
        <span>自定义</span>
      </div>
    )
  }

  const handleChange = (current: number) => {
    setPagination(prev => ({ ...prev, current }))
    props.onChange(current)
  }

  const { defaultMenu, paginationProps } = props
  return (
    <Spin spinning={props.loading}>
      {getExtraAddon()}
      {defaultMenu}
      <Divider style={{ margin: '4px 0' }} />
      <Pagination
        simple
        size='small'
        total={pagination.total}
        style={{ textAlign: 'center' }}
        {...paginationProps}
        current={pagination.current}
        onChange={handleChange}
      />
    </Spin>
  )
}

// Select 后的额外渲染
const AddonAfterRender = (props: {
  value: EasyRemoteProps['value'],
  addonAfter: ReactNode,
}) => {
  const copyText = useMemo(() => {
    if (Array.isArray(props.value)) return props.value.map(val => val.label).filter(val => !!val).join(',')
    return String(props.value?.label ?? '')
  }, [props.value])

  return (
    <div className='remote-select__addon'>
      {props.addonAfter}
      <div className='remote-select__addon-copy'>
        <Typography.Paragraph
          onClick={(evt: any) => evt.stopPropagation()}
          copyable={copyText ? { text: copyText, tooltips: ['复制', '成功'] } : false}
        />
      </div>
    </div>
  )
}

const EasyRemoteSelect = (props: EasyRemoteProps) => {
  const fieldOption = useMemo<EasyRemoteProps['fieldOption']>(() => {
    return merge({
      data: 'Data',
      total: 'TotalCount',
      value: 'ID',
      labels: ['NAME'],
      labelGapString: ['（', '）']
    }, props.fieldOption)
  }, [props.fieldOption])

  const [search, setSearch] = useState<EasyRemoteSearch>({
    value: '', // 搜索值
    current: 1, // 分页数据
  })

  const handleSearch = debounce((value) => {
    setSearch({ value, current: 1 })
  }, props.debounceTime ?? 500)

  const handleChange = (
    value: LabeledValue | LabeledValue[] | undefined,
    option: DefaultOptionType | DefaultOptionType[] | undefined
  ) => {
    /**
     * 单选时，清除选中，value 为 undefined
     * 多选时，清除选中，value 为 []
     */
    props.onChange?.(value, option)
    // 清除选中，重新发起搜索
    isEmpty(value) && setSearch({ value: '', current: 1 })
  }

  /**
   * 搜索结果数据
   */
  const [data, setData] = useState({ data: [], total: 0 })
  const [dataLoading, setDataLoading] = useState(false)

  const [focused, setFocused] = useState(!!props.forceRender)

  const promiseQueue = useRef<any[]>([])
  useEffect(() => {
    // 只在 focus 之后请求分页树数据
    if (!focused) return

    // 此处逻辑是为了保证调用顺序和接口返回数据一致，避免调用两次，而第二次的数据返回快于第一次的情况
    promiseQueue.current.push({
      doing: false,
      func: () => {
        if (props.remoteMethod instanceof Function) {
          return props.remoteMethod({
            ...search,
            remotePayload: props.remotePayload
          }).then((res: any) => {
            setData(prev => {
              const newData = {
                data: get(res, fieldOption!.data!) ?? [],
                total: get(res, fieldOption!.total!) ?? 0
              }
              return isEqual(prev, newData) ? prev : newData
            })
          })
        } else {
          return Promise.resolve()
        }
      }
    })

    function loop (arr: any) {
      if (!arr[0].doing) {
        arr[0].doing = true
        setDataLoading(true)
        arr[0].func().finally(() => {
          setDataLoading(false)
          arr.splice(0, 1)
          arr.length && loop(arr)
        })
      }
    }

    loop(promiseQueue.current)
  }, [focused, search])

  const pagination = useMemo(() => {
    return {
      total: data.total,
      current: search.current
    }
  }, [data.total, search.current])

  const { selectProps } = props
  return (
    <div className={`remote-select ${props.className}`} style={props.style}>
      <Select
        allowClear
        showSearch
        labelInValue
        loading={dataLoading}
        placeholder='请选择'
        filterOption={false}
        defaultActiveFirstOption={false}
        popupMatchSelectWidth={false}
        {...selectProps}
        optionLabelProp={fieldOption!.inputLabels?.length ? 'input' : 'title'}
        mode={props.multipleMode ? 'multiple' : undefined}
        value={props.value}
        onFocus={() => setFocused(true)}
        onSearch={handleSearch}
        onChange={handleChange}
        dropdownRender={menu => (
          <DropDownRender
            loading={selectProps?.loading || dataLoading}
            defaultMenu={menu}
            addonDropdown={props.addonDropdown}
            pagination={pagination}
            paginationProps={props.paginationProps}
            onChange={current => setSearch(prev => ({ ...prev, current }))}
          />
        )}
        className={`remote-select__select ${selectProps?.className}`}
      >
        {
          data.data.map(item => {
            const { value: keyField, labels, inputLabels, labelGapString } = fieldOption!
            const [gapF, gapE] = labelGapString!
            const getLabel = (fields?: string[]) => {
              return fields?.map((field, index) => {
                const target = get(item, field)
                return index === 0 ? target : `${gapF}${target}${gapE}`
              }).join('')
            }
            const label = getLabel(labels)
            const inputLabel = getLabel(inputLabels)
            const value = get(item, keyField!)
            return (
              <Select.Option key={value} value={value} title={label} input={inputLabel}>
                <div className='text-overflow remote-select__option'>
                  <span>{label}</span>
                  <Typography.Paragraph
                    onClick={(evt: any) => evt.stopPropagation()}
                    copyable={{ text: label, tooltips: ['复制', '成功'] }}
                    className='remote-select__copyitem'
                  />
                </div>
              </Select.Option>
            )
          })
        }
      </Select>
      <AddonAfterRender value={props.value} addonAfter={props.addonAfter} />
    </div>
  )
}

export default EasyRemoteSelect
